import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { LetterAvatar, initials } from "@/components/LetterAvatar";
import { isTile, markLayout, useLogoAnalysis, useToneMask, type LogoAnalysis } from "@/lib/logoPlate";
import { logoPlateImage } from "@/lib/projectImages";

/*
 * A project's logo and its builder's, drawn so both can be read at card size
 * (Andrew, 2026-10-23: "the logos should be legible at both sizes").
 *
 * The plate is white, in both themes, whatever the logo (Andrew, 2026-10-23:
 * "white plate in both themes, a tile logo shown whole as its own tile, nothing
 * else guessed"). A logo on transparency or on white has its empty margin
 * trimmed so the mark fills the plate; a logo uploaded as a coloured tile is
 * shown whole, as that tile — PropPay LX's maroon square stays a square. The
 * plate used to choose a white or near-black backing from how light the ink
 * measured, and the same SouthCal file came out on white on one card and on
 * dark grey on the next.
 *
 * Both are decorative (alt=""): the card names the project in its heading and
 * the builder in the line above it, and a screen reader hearing each twice
 * helps nobody.
 */

/** The plate behind every logo that is not a tile, in light and dark alike. */
const PLATE = "#ffffff";
/** A tile is drawn whole: its box is the image. */
const WHOLE = { x: 0, y: 0, w: 1, h: 1 };

/**
 * A window the size of the mark, with the image placed so only the mark shows.
 * Its size is a ceiling, not a fixed size: in a narrower card it scales down
 * with its proportions kept (the image is placed in percentages of the window),
 * so a plate never pushes the builder's mark off the card — which fixed pixel
 * sizes did in Console's 280px cards (Andrew, 2026-10-23).
 */
function Crop({ src, a, maxW, maxH, priority }: { src: string; a: LogoAnalysis; maxW: number; maxH: number; priority?: boolean }) {
  const m = markLayout(a, maxW, maxH);
  return (
    <span className="relative block min-w-0 overflow-hidden" style={{ width: m.width, maxWidth: "100%", aspectRatio: `${m.width} / ${m.height}` }}>
      <img
        src={src}
        alt=""
        // The same request mode as the analysis's own Image(), so one cache
        // entry serves both rather than a non-CORS copy tainting the read.
        crossOrigin="anonymous"
        decoding="async"
        loading={priority ? "eager" : "lazy"}
        draggable={false}
        className="absolute max-w-none select-none"
        style={{
          width: `${100 / a.box.w}%`,
          height: `${100 / a.box.h}%`,
          left: `${(-a.box.x / a.box.w) * 100}%`,
          top: `${(-a.box.y / a.box.h) * 100}%`,
        }}
      />
    </span>
  );
}

/**
 * The project's logo on its plate: one height for every project, whatever the
 * logo's shape, so the card's layout never moves with it — the builder mark
 * beside it is placed against the plate, and a short wide wordmark used to
 * make a short plate that pulled the builder up into the cover.
 */
export function LogoPlate({
  src,
  name,
  height,
  maxMarkWidth,
  maxMarkHeight,
  minWidth,
  padX,
  radius,
  priority,
  className,
}: {
  src?: string | null;
  /** The project's name: its initials stand in when there is no logo. */
  name: string;
  height: number;
  maxMarkWidth: number;
  maxMarkHeight: number;
  minWidth: number;
  padX: number;
  radius: number;
  priority?: boolean;
  className?: string;
}) {
  const a = useLogoAnalysis(src);
  // `min(…, 100%)`: the plate may be narrower than its usual minimum when the
  // card is — its slot caps it (CardLogos) and the mark inside scales to fit.
  const box: CSSProperties = { height, minWidth: `min(${minWidth}px, 100%)`, maxWidth: "100%", paddingInline: padX, borderRadius: radius };
  const base = cn("flex min-w-0 items-center justify-center", className);

  if (!src) {
    return (
      <div aria-hidden className={cn(base, "bg-secondary text-body-lg font-bold tracking-[.02em] text-secondary-foreground")} style={box}>
        {initials(name)}
      </div>
    );
  }
  if (!a || a === "failed") {
    // Not read yet (the first time this device sees the logo), or a host that
    // will not let it be read: the logo whole, on the same white plate.
    return (
      <div aria-hidden className={base} style={{ ...box, background: PLATE }}>
        <img src={src} alt="" crossOrigin="anonymous" decoding="async" className="block h-full w-auto max-w-full object-contain py-1" />
      </div>
    );
  }
  if (isTile(a)) {
    // The tile itself, at the plate's height, as wide as its proportions make
    // it up to the widest a plate goes; a wider tile fits that width and its
    // own colour fills the plate above and below.
    const tile = { ...a, box: WHOLE };
    const m = markLayout(tile, maxMarkWidth + padX * 2, height);
    return (
      <div aria-hidden className={base} style={{ ...box, width: m.width, minWidth: Math.min(m.width, height), paddingInline: 0, overflow: "hidden", background: a.ground }}>
        <Crop src={src} a={tile} maxW={m.width} maxH={height} priority={priority} />
      </div>
    );
  }
  // A wordmark is bound by the width; a crest stacked over its name is bound by
  // the height, and its lettering is a fraction of that — so it is given most
  // of the plate (Victoria Highlands' crest came out 42px wide at the wordmark's height).
  const aspect = (a.box.w / a.box.h) * a.ratio;
  // A near-square mark gets a square plate, the same height as every other —
  // a circle where the plate is a pill, with the mark inside the circle's
  // inscribed square so its corners are not cut.
  if (aspect < 1.4) {
    const side = radius * 2 >= height ? Math.round(height * 0.7) : height - 8;
    return (
      <div aria-hidden className={base} style={{ ...box, width: height, minWidth: height, paddingInline: 0, background: PLATE }}>
        <Crop src={src} a={a} maxW={side} maxH={side} priority={priority} />
      </div>
    );
  }
  const maxH = aspect < 2.2 ? Math.min(height - 10, Math.round(maxMarkHeight * 1.5)) : maxMarkHeight;
  return (
    <div aria-hidden className={base} style={{ ...box, background: PLATE }}>
      <Crop src={src} a={a} maxW={maxMarkWidth} maxH={maxH} priority={priority} />
    </div>
  );
}

/**
 * The builder's logo, opposite the project's plate: in one tone, on no plate.
 *
 * Every builder mark is drawn in the card's muted text colour through a mask
 * of its own shape and shading (lib/logoPlate.ts, `toneMask`), so it reads
 * on a light card and a dark one alike and sits quieter than the project's
 * full-colour plate (Andrew, 2026-10-23: greyscale, "I basically don't want
 * them to show on plates"). A near-square mark — a monogram over a name — is
 * drawn taller, since its lettering is a fraction of its height. The space is
 * kept while the mask draws; nothing at all until the logo has been read: it
 * is the quieter of the two, and the builder's name is printed on the card
 * either way.
 */
export function BuilderMark({
  src,
  height,
  maxWidth,
  className,
}: {
  src?: string | null;
  height: number;
  maxWidth: number;
  className?: string;
}) {
  const a = useLogoAnalysis(src);
  const read = a && a !== "failed" ? a : null;
  const aspect = read ? (read.box.w / read.box.h) * read.ratio : 1;
  const m = read ? markLayout(read, maxWidth, aspect < 2.2 ? Math.round(height * 1.55) : height) : null;
  const mask = useToneMask(src, read, m?.height ?? 0);
  if (!src || !a) return null;
  if (!m || mask === null) {
    // Not readable here: the logo itself, in grey.
    return <img src={src} alt="" crossOrigin="anonymous" className={cn("block w-auto max-w-full object-contain opacity-70 grayscale", className)} style={{ height: height + 6, maxWidth }} />;
  }
  return (
    <span
      aria-hidden
      // Its size is a ceiling: in a narrow card it shrinks, proportions kept,
      // rather than running off the card's edge.
      className={cn("block min-w-0 bg-current text-muted-foreground", className)}
      style={{
        width: m.width,
        maxWidth: "100%",
        aspectRatio: `${m.width} / ${m.height}`,
        ...(mask
          ? { WebkitMaskImage: `url("${mask}")`, maskImage: `url("${mask}")`, WebkitMaskSize: "100% 100%", maskSize: "100% 100%" }
          : { background: "none" }),
      }}
    />
  );
}

/**
 * The logo in the round marks that head a page and the menus — the page
 * header and the sidebar (the phone's menu sheet included). They stay circles
 * (Andrew, 2026-10-23: "Keep those circles!") and take the card's background
 * rules ("just apply the background rules, etc to the circles"), in PropPay and
 * SaleFish alike: a white disc in both themes, the logo trimmed so its mark
 * fills the circle, and a logo uploaded as a coloured tile on a disc of that
 * colour, its mark centred. No logo is LetterAvatar's initials, as before. It
 * reads the same 512 copy the card does (logoPlateImage).
 */
export function LogoDisc({ src: raw, name, size, className }: { src?: string | null; name: string; size: number; className?: string }) {
  const src = raw ? logoPlateImage(raw).src : null;
  const a = useLogoAnalysis(src);
  if (!src) return <LetterAvatar name={name} size={size} priority flat className={className} />;
  const base = cn("flex shrink-0 items-center justify-center overflow-hidden rounded-full", className);
  const box: CSSProperties = { width: size, height: size };
  if (!a || a === "failed") {
    // Not read yet, or not readable: the logo whole, inside the white disc.
    return (
      <div aria-hidden className={base} style={{ ...box, background: PLATE }}>
        <img src={src} alt="" crossOrigin="anonymous" decoding="async" className="block max-h-[62%] max-w-[78%] object-contain" />
      </div>
    );
  }
  // The mark's box fitted inside the circle: as wide as 80% of it, and no
  // corner outside a circle 86% across, so a squarish crest is not clipped.
  const aspect = (a.box.w / a.box.h) * a.ratio;
  let w = size * 0.8;
  let h = w / aspect;
  if (h > size * 0.8) { h = size * 0.8; w = h * aspect; }
  const k = Math.min(1, (size * 0.86) / Math.hypot(w, h));
  return (
    <div aria-hidden className={base} style={{ ...box, background: isTile(a) ? a.ground : PLATE }}>
      <Crop src={src} a={a} maxW={w * k} maxH={h * k} priority />
    </div>
  );
}
